import React, { useState, useEffect, useCallback, useRef } from "react";
import type { NativeScrollEvent, NativeSyntheticEvent } from "react-native";
import { View, StyleSheet, Pressable, ScrollView, ActivityIndicator } from "react-native";
import { Plus } from "@phosphor-icons/react";
import { ChevronDown } from "lucide-react";
import ThreadCard from "../ThreadCard/ThreadCard";
import CustomText from "../../common/general/CustomText/CustomText";
import { colors, radii, spacing } from "../../../styles/design-system";
import type { WorkspaceThreadItem } from "../types";
import { ThreadModals } from "./ThreadModals";

const MAX_VISIBLE_THREADS = 5;

export interface ThreadListProps {
  isExpanded: boolean;
  workspaceId?: string;
  threads: WorkspaceThreadItem[];
  totalCount?: number;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  onThreadSelect?: (threadId: string) => void;
  onNewThread?: (workspaceId: string) => void;
  onThreadPin?: (threadId: string) => void;
  onThreadRename?: (threadId: string, newName: string) => void;
  onThreadDelete?: (threadId: string) => void;
  activeThreadId?: string | null;
}

const ThreadList: React.FC<ThreadListProps> = ({
  isExpanded,
  workspaceId,
  threads,
  totalCount,
  hasMore,
  isLoadingMore = false,
  onLoadMore,
  onThreadSelect,
  onNewThread,
  onThreadPin,
  onThreadRename,
  onThreadDelete,
  activeThreadId,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [newThreadHovered, setNewThreadHovered] = useState(false);
  const [hoveredThreadId, setHoveredThreadId] = useState<string | null>(null);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedThread, setSelectedThread] = useState<WorkspaceThreadItem | null>(null);

  const prevActiveThreadIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isExpanded) {
      setShowAll(false);
      prevActiveThreadIdRef.current = null;
    }
  }, [isExpanded]);

  // Auto-expand when active thread is beyond visible slice
  useEffect(() => {
    if (!isExpanded || !activeThreadId) {
      return;
    }
    const activeThreadIndex = threads.findIndex((t) => t.id === activeThreadId);
    if (activeThreadIndex === -1) {
      return;
    }
    if (activeThreadIndex >= MAX_VISIBLE_THREADS) {
      setShowAll(true);
      if (prevActiveThreadIdRef.current !== activeThreadId) {
        prevActiveThreadIdRef.current = activeThreadId;
        if (onLoadMore && threads.length < (totalCount ?? threads.length)) {
          onLoadMore();
        }
      }
    }
  }, [isExpanded, activeThreadId, threads, onLoadMore, totalCount]);

  const resolvedTotalCount = totalCount ?? threads.length;
  const hasMoreThreads = resolvedTotalCount > MAX_VISIBLE_THREADS;
  const visibleThreads = showAll ? threads : threads.slice(0, MAX_VISIBLE_THREADS);
  const hiddenCount = Math.max(resolvedTotalCount - MAX_VISIBLE_THREADS, 0);
  const canLoadMore = (hasMore ?? threads.length < resolvedTotalCount) && showAll;

  const handleRenameClick = (threadId: string) => {
    const thread = threads.find((t) => t.id === threadId);
    if (thread) {
      setSelectedThread(thread);
      setRenameValue(thread.title);
      setRenameModalOpen(true);
    }
  };

  const handleRenameSubmit = () => {
    if (selectedThread && renameValue.trim() && onThreadRename) {
      onThreadRename(selectedThread.id, renameValue.trim());
      setRenameModalOpen(false);
      setRenameValue("");
      setSelectedThread(null);
    }
  };

  const handleDeleteClick = (threadId: string) => {
    const thread = threads.find((t) => t.id === threadId);
    if (thread) {
      setSelectedThread(thread);
      setDeleteModalOpen(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedThread && onThreadDelete) {
      onThreadDelete(selectedThread.id);
      setDeleteModalOpen(false);
      setSelectedThread(null);
    }
  };

  const handleShowAllToggle = () => {
    const nextShowAll = !showAll;
    setShowAll(nextShowAll);
    if (nextShowAll && onLoadMore && threads.length < resolvedTotalCount) {
      onLoadMore();
    }
  };

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (!canLoadMore || !onLoadMore || isLoadingMore) {
        return;
      }
      const { contentOffset, layoutMeasurement, contentSize } = event.nativeEvent ?? {};
      if (!contentOffset || !layoutMeasurement || !contentSize) {
        return;
      }
      const distanceFromBottom = contentSize.height - (contentOffset.y + layoutMeasurement.height);
      if (distanceFromBottom < 40) {
        onLoadMore();
      }
    },
    [canLoadMore, isLoadingMore, onLoadMore]
  );

  if (!isExpanded) {
    return null;
  }

  return (
    <View style={styles.container}>
      {onNewThread && workspaceId && (
        <Pressable
          style={[styles.newThreadCard, { backgroundColor: newThreadHovered ? colors.brand[50] : "transparent" }]}
          onPress={() => onNewThread(workspaceId)}
          onHoverIn={() => setNewThreadHovered(true)}
          onHoverOut={() => setNewThreadHovered(false)}
          accessibilityRole="button"
          accessibilityLabel="New thread">
          <View style={styles.newThreadMain}>
            <View style={styles.newThreadIconWrap}>
              <Plus size={16} color={colors.brand[600]} weight="bold" />
            </View>
            <CustomText
              text="New thread"
              size="s"
              weight="medium"
              useTranslationText={false}
              style={styles.newThreadTitle}
            />
          </View>
        </Pressable>
      )}

      {showAll ? (
        <ScrollView
          style={styles.scrollArea}
          contentContainerStyle={styles.scrollContent}
          onScroll={handleScroll}
          scrollEventThrottle={16}>
          {visibleThreads.map((thread) => (
            <ThreadCard
              key={thread.id}
              id={thread.id}
              title={thread.title}
              isActive={thread.isActive}
              pinned={thread.pinned}
              onSelect={onThreadSelect}
              onPin={onThreadPin}
              onRename={onThreadRename ? () => handleRenameClick(thread.id) : undefined}
              onDelete={onThreadDelete ? () => handleDeleteClick(thread.id) : undefined}
              onMouseEnter={() => setHoveredThreadId(thread.id)}
              onMouseLeave={() => setHoveredThreadId(null)}
            />
          ))}
          {isLoadingMore && (
            <View style={styles.loadingMore}>
              <ActivityIndicator size="small" color={colors.grey[500]} />
            </View>
          )}
        </ScrollView>
      ) : (
        visibleThreads.map((thread) => (
          <ThreadCard
            key={thread.id}
            id={thread.id}
            title={thread.title}
            isActive={thread.isActive}
            pinned={thread.pinned}
            onSelect={onThreadSelect}
            onPin={onThreadPin}
            onRename={onThreadRename ? () => handleRenameClick(thread.id) : undefined}
            onDelete={onThreadDelete ? () => handleDeleteClick(thread.id) : undefined}
            onMouseEnter={() => setHoveredThreadId(thread.id)}
            onMouseLeave={() => setHoveredThreadId(null)}
          />
        ))
      )}

      {hasMoreThreads && (
        <Pressable
          style={styles.showAllButton}
          onPress={handleShowAllToggle}
          accessibilityRole="button"
          accessibilityLabel={showAll ? "Show less" : `Show all (${hiddenCount} more)`}>
          <View style={[styles.chevronIcon, showAll && styles.chevronIconRotated]}>
            <ChevronDown size={16} color={colors.grey[500]} />
          </View>
          <CustomText
            text={showAll ? "Show less" : `Show all (${hiddenCount} more)`}
            size="s"
            weight="medium"
            useTranslationText={false}
            style={styles.showAllText}
          />
        </Pressable>
      )}

      <ThreadModals
        renameModalOpen={renameModalOpen}
        setRenameModalOpen={setRenameModalOpen}
        renameValue={renameValue}
        setRenameValue={setRenameValue}
        onRenameSubmit={handleRenameSubmit}
        deleteModalOpen={deleteModalOpen}
        setDeleteModalOpen={setDeleteModalOpen}
        selectedThreadTitle={selectedThread?.title}
        onDeleteConfirm={handleDeleteConfirm}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginLeft: 24,
    paddingLeft: spacing.sm,
    paddingTop: 2,
    borderLeftWidth: 2,
    borderLeftColor: colors.grey[200],
    gap: 2,
  },
  scrollArea: {
    maxHeight: 260,
  },
  scrollContent: {
    gap: 2,
  },
  newThreadCard: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: radii.lg,
    gap: spacing.xs,
  },
  newThreadMain: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  newThreadIconWrap: {
    flexShrink: 0,
  },
  newThreadTitle: {
    color: colors.brand[600],
  },
  showAllButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: radii.lg,
    gap: spacing.xs,
  },
  chevronIcon: {
    flexShrink: 0,
  },
  chevronIconRotated: {
    transform: [{ rotate: "180deg" }],
  },
  showAllText: {
    color: colors.grey[500],
  },
  loadingMore: {
    paddingVertical: 8,
    alignItems: "center",
  },
});

export default ThreadList;
